import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Cable, MessagesSquare, Pencil, Plus, Power, PowerOff, Trash2, Zap } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import type { Connector } from "@/api/generated/Connector"
import {
  useConnectors,
  useConnectorTypes,
  useDeleteConnector,
  useTestConnector,
  useUpdateConnector,
} from "@/api/queries/connectors"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ConnectorSheet } from "@/features/connectors/connector-sheet"
import { ConnectorStatusBadge } from "@/features/connectors/connector-status"
import { CONNECTOR_STATE_RANK, connectorState } from "@/features/connectors/logic"
import { ConnectorTypePicker } from "@/features/connectors/type-picker"
import { useConnectorEvents } from "@/features/connectors/use-connector-events"
import { fire } from "@/features/studio/fire"

interface ConnectorsSearch {
  /** Connector open in the editor. */
  connector?: number
  /** Type of a connector being created. */
  new?: string
}

export const Route = createFileRoute("/_app/studio/connectors/")({
  validateSearch: (s: Record<string, unknown>): ConnectorsSearch => ({
    connector: Number(s.connector) > 0 ? Number(s.connector) : undefined,
    new: typeof s.new === "string" && s.new ? s.new : undefined,
  }),
  component: ConnectorsPage,
})

function ConnectorsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  // live runtime status; polling only while the feed is down
  const live = useConnectorEvents() === "open"
  const connectors = useConnectors(!live)
  const types = useConnectorTypes()
  const update = useUpdateConnector()
  const remove = useDeleteConnector()
  const test = useTestConnector()
  const confirm = useConfirm()
  const [picking, setPicking] = useState(false)
  const [testing, setTesting] = useState<number>()

  const setSearch = (patch: Partial<ConnectorsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const typeOf = useMemo(() => {
    const m = new Map((types.data ?? []).map((s) => [s.type, s]))
    return (type: string) => m.get(type)
  }, [types.data])
  const typeLabel = useCallback((type: string) => typeOf(type)?.label ?? type, [typeOf])

  const editing = connectors.data?.find((c) => c.id === search.connector)
  const newType = search.new ? typeOf(search.new) : undefined
  const sheetOpen = !!newType || !!editing

  const runTest = async (c: Connector) => {
    setTesting(c.id)
    try {
      const r = await test.mutateAsync(c.id)
      if (r.ok) toast.success(t("studio.connectors.test.okNamed", { name: c.name }))
      else
        toast.error(t("studio.connectors.test.failedNamed", { name: c.name }), {
          description: r.error ?? undefined,
        })
    } finally {
      setTesting(undefined)
    }
  }

  const askDelete = (c: Connector) =>
    void confirm({
      title: t("studio.connectors.delete.title", { name: c.name }),
      description: t("studio.connectors.delete.description"),
      confirmLabel: t("studio.connectors.delete.confirm"),
      confirmText: c.name,
      destructive: true,
      onConfirm: async () => {
        await remove.mutateAsync(c)
        if (search.connector === c.id) setSearch({ connector: undefined })
      },
    })

  const columns = useMemo(
    () =>
      createColumns<Connector>((c) => [
        c.accessor("enabled", {
          header: t("studio.connectors.columns.enabled"),
          meta: { label: t("studio.connectors.columns.enabled") },
          enableSorting: false,
          cell: ({ row }) => {
            const x = row.original
            const blocked = !x.enabled && (!x.implemented || x.agent_id == null)
            const toggle = (
              <Switch
                checked={x.enabled}
                disabled={blocked}
                aria-label={`${t("studio.connectors.columns.enabled")}: ${x.name}`}
                onCheckedChange={(v) => update.mutate({ id: x.id, enabled: v, quiet: true })}
                data-testid="connector-enabled"
              />
            )
            return (
              <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                {blocked && x.implemented ? (
                  <Tooltip>
                    <TooltipTrigger render={<span className="inline-flex" />}>{toggle}</TooltipTrigger>
                    <TooltipContent>{t("studio.connectors.enableBlocked")}</TooltipContent>
                  </Tooltip>
                ) : (
                  toggle
                )}
              </span>
            )
          },
        }),
        c.accessor("name", {
          header: t("studio.connectors.columns.connector"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="flex max-w-md min-w-48 items-center gap-3">
              <div className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-lg">
                <Cable className="size-4" />
              </div>
              <div className="min-w-0">
                <div className="truncate font-medium">{row.original.name}</div>
                <div className="text-muted-foreground truncate text-xs">{typeLabel(row.original.type)}</div>
              </div>
            </div>
          ),
        }),
        c.accessor((x) => x.agent_name ?? "", {
          id: "agent",
          header: t("studio.connectors.columns.agent"),
          meta: { label: t("studio.connectors.columns.agent") },
          cell: ({ row }) =>
            row.original.agent_name ? (
              <span className="truncate">{row.original.agent_name}</span>
            ) : (
              <span className="text-muted-foreground text-xs">{t("studio.connectors.noAgent")}</span>
            ),
        }),
        c.accessor((x) => CONNECTOR_STATE_RANK[connectorState(x)], {
          id: "status",
          header: t("studio.connectors.columns.status"),
          meta: { label: t("studio.connectors.columns.status") },
          cell: ({ row }) => (
            <ConnectorStatusBadge connector={row.original} typeLabel={typeLabel(row.original.type)} />
          ),
        }),
        c.accessor("updated_at", {
          header: t("studio.connectors.columns.updated"),
          meta: { label: t("studio.connectors.columns.updated") },
          cell: ({ getValue }) => (
            <RelativeTime value={getValue()} className="text-muted-foreground text-xs" />
          ),
        }),
      ]),
    [t, typeLabel, update.mutate],
  )

  const newButton = (size?: "sm") => (
    <Button size={size} onClick={() => setPicking(true)} data-testid="new-connector">
      <Plus />
      {t("studio.connectors.new")}
    </Button>
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.connectors.title")}
        description={t("studio.connectors.description")}
        icon={Cable}
        actions={newButton()}
      />
      <DataTable
        label={t("studio.connectors.title")}
        data={connectors.data}
        columns={columns}
        getRowId={(c) => String(c.id)}
        loading={connectors.isPending}
        error={connectors.error}
        onRetry={() => void connectors.refetch()}
        searchable
        searchPlaceholder={t("studio.connectors.search")}
        initialSorting={[{ id: "name", desc: false }]}
        columnToggle
        persistKey="studio-connectors"
        onRowClick={(c) => setSearch({ connector: c.id, new: undefined })}
        isRowActive={(c) => c.id === search.connector}
        rowActions={(c) => (
          <>
            <DropdownMenuItem
              onClick={() =>
                void navigate({
                  to: "/studio/connectors/$connectorId",
                  params: { connectorId: String(c.id) },
                })
              }
            >
              <MessagesSquare />
              {t("studio.connectors.actions.conversations")}
            </DropdownMenuItem>
            {c.implemented && (
              <DropdownMenuItem onClick={() => fire(runTest(c))} disabled={testing === c.id}>
                <Zap />
                {t("studio.connectors.actions.test")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setSearch({ connector: c.id, new: undefined })}>
              <Pencil />
              {t("common.edit")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => update.mutate({ id: c.id, enabled: !c.enabled, quiet: true })}
              disabled={!c.enabled && (!c.implemented || c.agent_id == null)}
            >
              {c.enabled ? <PowerOff /> : <Power />}
              {c.enabled ? t("studio.connectors.actions.disable") : t("studio.connectors.actions.enable")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={() => askDelete(c)}>
              <Trash2 />
              {t("studio.connectors.actions.delete")}
            </DropdownMenuItem>
          </>
        )}
        emptyState={
          <EmptyState
            variant="plain"
            icon={Cable}
            title={t("studio.connectors.empty.title")}
            description={t("studio.connectors.empty.description")}
            action={newButton("sm")}
          />
        }
      />
      <ConnectorTypePicker
        open={picking}
        onOpenChange={setPicking}
        types={types.data}
        loading={types.isPending}
        onPick={(s) => {
          setPicking(false)
          setSearch({ new: s.type, connector: undefined })
        }}
      />
      <ConnectorSheet
        open={sheetOpen}
        connector={newType ? undefined : editing}
        type={newType ?? (editing ? typeOf(editing.type) : undefined)}
        onOpenChange={(o) => !o && setSearch({ connector: undefined, new: undefined })}
        onCreated={(c) => setSearch({ new: undefined, connector: c.id })}
      />
    </PageContainer>
  )
}
